import { useBetsOriginalsMe } from '@/api/bets/queries/useBetsOriginalsMe';
import { useGameDifficulties } from '@/api/game-difficulties';
import { GameID } from '@/enums/gameId';
import { GameTypeId } from '@/enums/gameTypeId';
import { RootState } from '@/lib/store';
import { handleDifficulty } from '@/lib/store/games-preferences/actions';
import { setMineQuantity } from '@/lib/store/mines/actions';
import { BetStatusId } from '@/models/bet/status/id';
import { GameDifficulty } from '@/models/game/difficulty';
import { MinesGameDifficulty } from '@/models/mines/game-difficulty';
import { MissionUncrossableGameDifficulty } from '@/models/mission-uncrossable/game-difficulty';
import { TowerGameDifficulty } from '@/models/tower/game-difficulty';
import { FormGroup, Input, SelectInput } from '@starsoft/common/components';
import useTranslation from 'next-translate/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { DifficultySelectorProps } from './props';

export default function DifficultySelector({
  gameTypeId,
}: DifficultySelectorProps) {
  const dispatch = useDispatch();
  const { lang, t } = useTranslation('mines');
  const difficultyId = useSelector(
    (state: RootState) => state.gamePreferences.difficultyId,
  );
  const minesQuantity: number = useSelector(
    (state: RootState) => state.mines.mineQuantity,
  );

  const { gameDifficulties, isPending, error, refetch } = useGameDifficulties({
    gameTypeId,
  });

  const { bet } = useBetsOriginalsMe({
    gameId: GameID.Mines,
  });

  const isDisabled =
    !!bet &&
    bet?.status?.status?.id?.toString() === BetStatusId.Open?.toString();

  const handleChangeDifficulty = (
    difficulty:
      | GameDifficulty
      | MinesGameDifficulty
      | TowerGameDifficulty
      | MissionUncrossableGameDifficulty,
  ) => {
    if (difficulty instanceof GameDifficulty) {
      dispatch(handleDifficulty(difficulty.id));
      return;
    }

    dispatch(handleDifficulty(difficulty.difficultyId));
  };

  function handleChangeMineQuantity(
    e: React.ChangeEvent<HTMLInputElement>,
  ): void {
    const mines = Number(e.target.value);

    if (isDisabled) {
      return;
    }

    const data = (gameDifficulties as MinesGameDifficulty[])?.find(
      d => d.minesCount === mines,
    )?.difficultyId;

    dispatch(setMineQuantity(mines));
    dispatch(handleDifficulty(data));
  }

  if (gameTypeId?.toString() === GameTypeId.Mines.toString()) {
    return (
      <FormGroup label={t('mines_quantity_placeholder') as string}>
        <Input
          type="range"
          value={Number(minesQuantity)}
          min={2}
          step={1}
          hideLock
          disabled={isDisabled}
          max={24}
          onInput={handleChangeMineQuantity}
        />
      </FormGroup>
    );
  }

  return (
    <FormGroup label={t('tower:tower_dificulty_placeholder') as string}>
      <SelectInput
        options={gameDifficulties ?? []}
        getOptionLabel={option =>
          option instanceof GameDifficulty
            ? option?.name?.[lang]
            : option?.difficulty?.name?.[lang]
        }
        getOptionValue={option =>
          option instanceof GameDifficulty ? option?.id : option?.difficultyId
        }
        setValue={handleChangeDifficulty}
        value={difficultyId}
        loading={isPending}
        error={error}
        refetch={refetch}
      />
    </FormGroup>
  );
}
